<template>
  <div ref="container" class="tooltip-container">
    <slot name="trigger" />
    <div ref="content">
      <slot name="content" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { useTippy } from 'vue-tippy/composition'

type Position = 'top' | 'right' | 'bottom' | 'left'
type Theme = 'default' | 'coachmark'
type ArrowPosition = 'start' | 'center' | 'end'
type Trigger = 'click' | 'manual' | 'mouseenter focus'

type TooltipProps = {
  position?: Position
  theme?: Theme
  arrow?: boolean
  arrowPosition?: ArrowPosition
  trigger?: Trigger
  hideOnClick?: boolean
  visible?: boolean
  interactive?: boolean
  flip?: boolean
  maxWidth?: string
  hideOnContentClick?: boolean
}

const props = withDefaults(defineProps<TooltipProps>(), {
  arrow: true,
  arrowPosition: 'center',
  defaultStyle: true,
  hideOnClick: true,
  interactive: false,
  maxWidth: '350px',
  position: 'top',
  theme: 'default',
  trigger: 'mouseenter focus',
  visible: false,
  flip: true,
  hideOnContentClick: false,
})

const container = ref<HTMLElement | null>(null)
const content = ref<HTMLElement | null>(null)

const placement = computed(() => {
  const isArrowPositionCentre = props.arrowPosition !== 'center'
  const placement = isArrowPositionCentre
    ? `${props.position}-${props.arrowPosition}`
    : props.position

  return placement
})

const config = reactive({
  appendTo: 'parent',
  arrow: props.arrow,
  content,
  flip: props.flip,
  hideOnClick: props.hideOnClick,
  interactive: props.interactive,
  maxWidth: props.maxWidth,
  theme: props.theme,
  trigger: props.trigger,
  showOnInit: props.visible,
  placement: placement.value,
})

const { tippy } = useTippy(container, config)

onMounted(() => {
  if (props.hideOnContentClick) {
    content.value?.addEventListener('click', onContentClick)
  }
})

const onContentClick = () => {
  if (props.hideOnContentClick) {
    tippy.value?.hide()
  }
}
</script>

<style lang="scss">
@use '@/styles/baseTooltip.scss' as *;
</style>
